import React, { useEffect, useMemo, useRef, useState } from 'react';
import { teacherApi } from '../../services/api';
import { BusyRange, overlapsBusy, formatBusyRanges } from '../../utils/appointmentAvailability';

const F = { fontFamily: 'Inter' } as const;

interface AppointmentRow {
  id: number | string;
  appointmentDate?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  appointment_type?: string | null;
  format?: string | null;
  status?: string;
  notes?: string | null;
  _createdByTeacher?: boolean;
  users_appointments_requester_user_idTousers?: { first_name?: string; last_name?: string } | null;
  users_appointments_student_user_idTousers?: { uuid?: string; first_name?: string; last_name?: string } | null;
  subjects?: { name?: string } | null;
}

interface StudentOption { id: string; uuid: string; name: string; className: string; parents?: { id: string; name: string }[] }

const STATUS_LABEL: Record<string, string> = { pending: 'Onay Bekliyor', pending_parent_approval: 'Veli Onayı Bekliyor', confirmed: 'Onaylandı', cancelled: 'İptal Edildi', completed: 'Tamamlandı' };
const FORMAT_LABEL: Record<string, string> = { face_to_face: 'Yüz Yüze', online: 'Online', phone: 'Telefon' };
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled' | 'completed';
const FILTER_LABEL: Record<StatusFilter, string> = { all: 'Tümü', pending: 'Onay Bekleyen', confirmed: 'Onaylanan', cancelled: 'İptal Edilen', completed: 'Tamamlanan' };

const CloseX: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick}
    className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-200 flex items-center justify-center">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2b2b2b" strokeWidth="3" strokeLinecap="round">
      <line x1="4" y1="4" x2="20" y2="20" /><line x1="20" y1="4" x2="4" y2="20" />
    </svg>
  </button>
);

const TeacherAppointments: React.FC = () => {
  const [appointments, setAppointments] = useState<AppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [formatFilter, setFormatFilter] = useState<'all' | 'face_to_face' | 'online' | 'phone'>('all');
  // Class + student filter: pick a class first, then optionally a student from it.
  const [classFilter, setClassFilter] = useState<string>('all');
  // Student combobox keeps free-text search + exact selection (by uuid).
  const [studentSearch, setStudentSearch] = useState('');
  const [studentFilterUuid, setStudentFilterUuid] = useState<string | null>(null);
  const [studentFilterOpen, setStudentFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  // Inline per-appointment note editing (teacher-only).
  const [editingNoteId, setEditingNoteId] = useState<string | number | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Create appointment form state
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [targetType, setTargetType] = useState<'student' | 'class' | 'parent'>('student');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [studentDropOpen, setStudentDropOpen] = useState(false);
  const studentSearchRef = useRef<HTMLDivElement | null>(null);
  const [aptTitle, setAptTitle] = useState('');
  const [aptDate, setAptDate] = useState('');
  const [aptStartTime, setAptStartTime] = useState('09:00');
  const [aptEndTime, setAptEndTime] = useState('09:30');
  // B7: the teacher's own busy ranges for the picked date (derived from the
  // already-loaded appointment list), so they see if they are double-booking.
  const busy = useMemo<BusyRange[]>(() => {
    if (!aptDate) return [];
    const toHHMM = (s?: string | null) => {
      if (!s) return null;
      const d = new Date(s);
      if (isNaN(d.getTime())) return null;
      return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
    };
    return appointments
      .filter((a) => (a.appointmentDate ?? '').slice(0, 10) === aptDate)
      .filter((a) => ['pending', 'pending_parent_approval', 'confirmed'].includes(a.status ?? ''))
      .map((a) => ({ start_time: toHHMM(a.start_time), end_time: toHHMM(a.end_time) }))
      .filter((r): r is BusyRange => !!r.start_time && !!r.end_time);
  }, [appointments, aptDate]);
  const conflict = useMemo(() => overlapsBusy(aptStartTime, aptEndTime, busy), [aptStartTime, aptEndTime, busy]);
  const [aptFormat, setAptFormat] = useState('face_to_face');
  const [aptNotes, setAptNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createDone, setCreateDone] = useState(false);

  const load = () => {
    setLoading(true);
    teacherApi.appointments()
      .then((res) => setAppointments(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const saveNote = async (id: string | number) => {
    setSavingNote(true);
    try {
      await teacherApi.setAppointmentNotes(id, noteDraft);
      setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, notes: noteDraft.trim() ? noteDraft : null } : a)));
      setEditingNoteId(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingNote(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Load the canonical student list once so the list-filter combobox can offer
  // selectable students (same source as the create modal).
  useEffect(() => {
    teacherApi.studentsForAppointment().then((res) => setStudents(res.data || [])).catch(() => {});
  }, []);

  // Distinct classes the teacher can schedule with (drives the Sınıf dropdown).
  const classOptions = useMemo(
    () => Array.from(new Set(students.map((s) => s.className).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'tr')),
    [students],
  );
  // Student combobox is scoped to the selected class; matches the typed query.
  const studentFilterMatches = useMemo(() => {
    if (classFilter === 'all') return [];
    const inClass = students.filter((s) => s.className === classFilter);
    const q = studentSearch.trim().toLocaleLowerCase('tr');
    const base = q ? inClass.filter((s) => s.name.toLocaleLowerCase('tr').includes(q)) : inClass;
    return base.slice(0, 50);
  }, [students, studentSearch, classFilter]);
  const selectedFilterStudent = students.find((s) => s.uuid === studentFilterUuid) ?? null;

  useEffect(() => {
    if (!studentFilterOpen) return;
    const onClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setStudentFilterOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [studentFilterOpen]);

  const openCreate = () => {
    setCreateOpen(true);
    setCreateError(null);
    setCreateDone(false);
    setAptTitle(''); setAptDate(''); setAptStartTime('09:00'); setAptEndTime('09:30');
    setAptFormat('face_to_face'); setAptNotes('');
    setSelectedStudentId(''); setSelectedClassId(''); setSelectedParentId('');
    setStudentQuery(''); setStudentDropOpen(false);
    setTargetType('student');

    setLoadingStudents(true);
    teacherApi.studentsForAppointment()
      .then((res) => setStudents(res.data || []))
      .catch(() => setStudents([]))
      .finally(() => setLoadingStudents(false));
  };

  const handleCreate = async () => {
    if (!aptTitle.trim() || !aptDate) { setCreateError('Başlık ve tarih zorunludur.'); return; }
    if ((targetType === 'student' || targetType === 'parent') && !selectedStudentId) { setCreateError('Lütfen bir öğrenci seçin.'); return; }
    const parentAppt = targetType === 'parent';
    const parents = selectedStudent?.parents ?? [];
    const parentId = parentAppt ? (selectedParentId || parents[0]?.id) : undefined;
    if (parentAppt && !parentId) { setCreateError('Seçilen öğrencinin kayıtlı velisi yok.'); return; }
    setSubmitting(true);
    setCreateError(null);
    try {
      const dateTime = new Date(`${aptDate}T${aptStartTime}`);
      const endDt = new Date(`${aptDate}T${aptEndTime}`);
      const payload: any = {
        title: aptTitle,
        description: aptNotes || undefined,
        appointmentDate: dateTime.toISOString(),
        startTime: dateTime.toISOString(),
        endTime: endDt.toISOString(),
        format: aptFormat,
      };
      if (targetType === 'class') {
        payload.classId = selectedClassId;
      } else {
        payload.studentUserId = selectedStudentId;
        if (parentAppt) payload.parentUserId = parentId;
      }

      await teacherApi.createAppointment(payload);
      setCreateDone(true);
      load();
    } catch (e: any) {
      setCreateError(e?.response?.data?.error ?? e.message ?? 'Randevu oluşturulamadı.');
    } finally {
      setSubmitting(false);
    }
  };

  // Derive unique classes from student list
  const uniqueClasses = Array.from(
    new Map(students.map(s => [s.className, s.className])).entries()
  ).map(([name]) => ({ id: name, name }));

  // Typeahead match for student picker
  const studentMatches = useMemo(() => {
    const q = studentQuery.trim().toLocaleLowerCase('tr');
    if (!q) return students.slice(0, 12);
    return students.filter(s =>
      s.name.toLocaleLowerCase('tr').includes(q) ||
      s.className.toLocaleLowerCase('tr').includes(q)
    ).slice(0, 20);
  }, [students, studentQuery]);

  const selectedStudent = students.find(s => s.id === selectedStudentId);

  // Outside click closes dropdown
  useEffect(() => {
    if (!studentDropOpen) return;
    const onClick = (e: MouseEvent) => {
      if (studentSearchRef.current && !studentSearchRef.current.contains(e.target as Node)) setStudentDropOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [studentDropOpen]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ ...F, fontSize: 24, fontWeight: 600, color: '#2b2b2b' }}>Randevularım</h1>
        <button onClick={openCreate}
          className="rounded-[12px] px-5 py-3 flex items-center gap-2 hover:opacity-90"
          style={{ background: '#3973bf' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#fafafa" strokeWidth="2.5">
            <line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" />
          </svg>
          <span style={{ ...F, fontSize: 16, fontWeight: 700, color: '#fafafa' }}>Randevu Oluştur</span>
        </button>
      </div>

      <div className="mb-5 space-y-3">
        {/* Class + student filter — pick a class first, then optionally a student
            from that class. The student box is a free-text combobox scoped to the class. */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-[12px] font-semibold text-gray-500 mb-1" style={F}>Sınıf</label>
            <select
              value={classFilter}
              onChange={(e) => { setClassFilter(e.target.value); setStudentFilterUuid(null); setStudentSearch(''); setStudentFilterOpen(false); }}
              className="bg-white rounded-[10px] border border-gray-200 px-3 py-2 text-[13px] text-[#2b2b2b] outline-none focus:border-[#3973bf] min-w-[180px]"
              style={F}
            >
              <option value="all">Tüm Sınıflar</option>
              {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-500 mb-1" style={F}>Öğrenci</label>
            <div ref={filterRef} className="relative w-[280px]">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                disabled={classFilter === 'all'}
                value={studentFilterOpen ? studentSearch : (selectedFilterStudent ? selectedFilterStudent.name : studentSearch)}
                onChange={(e) => { setStudentSearch(e.target.value); setStudentFilterUuid(null); setStudentFilterOpen(true); }}
                onFocus={() => { if (classFilter === 'all') return; setStudentFilterOpen(true); if (selectedFilterStudent) setStudentSearch(''); }}
                placeholder={classFilter === 'all' ? 'Önce sınıf seçin' : 'Öğrenci ara veya seç…'}
                className="w-full bg-white rounded-full border border-gray-200 pl-9 pr-9 py-2 text-[13px] outline-none focus:border-[#3973bf] disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:placeholder-gray-400"
                style={F}
              />
              {(studentSearch || studentFilterUuid) && classFilter !== 'all' && (
                <button onClick={() => { setStudentSearch(''); setStudentFilterUuid(null); setStudentFilterOpen(false); }} aria-label="Temizle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-[18px] w-5 h-5 leading-none">×</button>
              )}
              {studentFilterOpen && classFilter !== 'all' && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-[12px] shadow-lg border border-gray-200 z-30 max-h-[240px] overflow-y-auto">
                  {studentFilterMatches.length === 0 ? (
                    <p className="text-center text-gray-400 py-3 text-[13px]">Kayıt bulunamadı</p>
                  ) : (
                    studentFilterMatches.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => { setStudentFilterUuid(s.uuid); setStudentSearch(''); setStudentFilterOpen(false); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        style={F}
                      >
                        <p className="text-[14px] font-semibold text-[#2b2b2b]">{s.name}</p>
                        <p className="text-[12px] text-gray-500">{s.className}</p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Status filter */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[12px] font-semibold text-gray-500 mr-1">Durum:</span>
          {(Object.keys(FILTER_LABEL) as StatusFilter[]).map((k) => {
            const active = statusFilter === k;
            return (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                className={`text-[12px] lg:text-[13px] font-semibold px-4 py-1.5 rounded-full border transition-colors ${
                  active ? 'bg-[#3973bf] text-white border-[#3973bf]' : 'bg-white text-[#2b2b2b] border-gray-200 hover:bg-gray-50'
                }`}
              >
                {FILTER_LABEL[k]}
              </button>
            );
          })}
        </div>

        {/* Format filter — Yüz Yüze / Online / Telefon */}
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-[12px] font-semibold text-gray-500 mr-1">Format:</span>
          {([['all', 'Tümü'], ['face_to_face', 'Yüz Yüze'], ['online', 'Online'], ['phone', 'Telefon']] as const).map(([k, label]) => {
            const active = formatFilter === k;
            return (
              <button
                key={k}
                onClick={() => setFormatFilter(k)}
                className={`text-[12px] lg:text-[13px] font-semibold px-4 py-1.5 rounded-full border transition-colors ${
                  active ? 'bg-[#e5599b] text-white border-[#e5599b]' : 'bg-white text-[#2b2b2b] border-gray-200 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : (() => {
        const q = studentSearch.trim().toLocaleLowerCase('tr');
        const classUuids = classFilter === 'all'
          ? null
          : new Set(students.filter((s) => s.className === classFilter).map((s) => s.uuid));
        const filtered = appointments.filter((a) => {
          if (statusFilter !== 'all' && a.status !== statusFilter) return false;
          if (formatFilter !== 'all' && a.format !== formatFilter) return false;
          const apptStudentUuid = a.users_appointments_student_user_idTousers?.uuid;
          // Class scope (when a class is chosen): only its students' appointments.
          if (classUuids && (!apptStudentUuid || !classUuids.has(apptStudentUuid))) return false;
          // Optional narrowing to a single student from that class.
          if (studentFilterUuid && apptStudentUuid !== studentFilterUuid) return false;
          // Free-text search within the class (when no student is explicitly picked).
          if (!studentFilterUuid && q) {
            const s = a.users_appointments_student_user_idTousers;
            const r = a.users_appointments_requester_user_idTousers;
            const sName = s ? `${s.first_name ?? ''} ${s.last_name ?? ''}`.toLocaleLowerCase('tr') : '';
            const rName = r ? `${r.first_name ?? ''} ${r.last_name ?? ''}`.toLocaleLowerCase('tr') : '';
            if (!sName.includes(q) && !rName.includes(q)) return false;
          }
          return true;
        });
        if (filtered.length === 0) {
          return (
            <p className="text-center text-gray-400 py-16">
              {appointments.length === 0
                ? 'Henüz randevunuz yok.'
                : 'Filtrelerle eşleşen randevu bulunamadı.'}
            </p>
          );
        }
        return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((a) => {
            const d = a.appointmentDate ? new Date(a.appointmentDate) : null;
            const fmtUtcTime = (s: string | null | undefined) => {
              if (!s) return null;
              const dt = new Date(s);
              return dt.getUTCHours().toString().padStart(2, '0') + ':' + dt.getUTCMinutes().toString().padStart(2, '0');
            };
            const timeStr = fmtUtcTime(a.start_time) ?? '—';
            const student = a.users_appointments_student_user_idTousers;
            const studentName = student ? `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim() : null;
            const requester = a.users_appointments_requester_user_idTousers;
            const requesterName = requester ? `${requester.first_name ?? ''} ${requester.last_name ?? ''}`.trim() : null;
            return (
              <div key={a.id} className="bg-[#fafafa] rounded-[20px] p-5"
                style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="flex items-center justify-between mb-3">
                  <p style={{ ...F, fontSize: 18, fontWeight: 700, color: '#2b2b2b' }}>
                    {studentName ?? requesterName ?? 'Randevu'}
                  </p>
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${
                    a.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    a.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    a.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{STATUS_LABEL[a.status ?? ''] ?? a.status}</span>
                </div>
                <div className="space-y-1 text-[13px] text-[#2b2b2b] mb-3">
                  {a.format && <p>Format: {FORMAT_LABEL[a.format] ?? a.format}</p>}
                  {a.subjects?.name && <p>Ders: {a.subjects.name}</p>}
                  <p>Tarih: {d ? d.toLocaleDateString('tr-TR') : '—'}</p>
                  <p>Saat: {timeStr}</p>
                </div>

                {/* Teacher note — add / edit inline (persists to appointments.notes). */}
                <div className="mb-3">
                  {editingNoteId === a.id ? (
                    <div>
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        rows={2}
                        placeholder="Randevu notu…"
                        className="w-full bg-white rounded-[10px] px-3 py-2 text-[13px] outline-none resize-none"
                        style={{ border: '2px solid #3973bf', ...F }}
                      />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => saveNote(a.id)} disabled={savingNote}
                          className="rounded-[8px] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                          style={{ background: '#3973bf', ...F }}>
                          {savingNote ? 'Kaydediliyor…' : 'Kaydet'}
                        </button>
                        <button onClick={() => setEditingNoteId(null)}
                          className="rounded-[8px] px-3 py-1.5 text-[12px] font-semibold text-[#2b2b2b] hover:opacity-90"
                          style={{ background: '#e5e5e5', ...F }}>
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : a.notes ? (
                    <div className="flex items-start justify-between gap-2">
                      <p className="italic text-gray-500 text-[12px]">{a.notes}</p>
                      <button onClick={() => { setEditingNoteId(a.id); setNoteDraft(a.notes ?? ''); }}
                        className="shrink-0 text-[12px] font-semibold text-[#3973bf] hover:underline">
                        Düzenle
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingNoteId(a.id); setNoteDraft(''); }}
                      className="text-[12px] font-semibold text-[#3973bf] hover:underline">
                      + Not Ekle
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {/* Only show approve/cancel if the student (not the teacher) requested the appointment. */}
                  {a.status === 'pending' && !a._createdByTeacher && (
                    <>
                      <button
                        onClick={() => teacherApi.updateAppointment(a.id, 'confirmed').then(load).catch(console.error)}
                        className="flex-1 rounded-[10px] py-2 text-[13px] font-semibold hover:opacity-90"
                        style={{ background: '#59e56e', color: '#fff', ...F }}>
                        Onayla
                      </button>
                      <button
                        onClick={() => teacherApi.updateAppointment(a.id, 'cancelled').then(load).catch(console.error)}
                        className="flex-1 rounded-[10px] py-2 text-[13px] font-semibold hover:opacity-90"
                        style={{ background: '#e5599b', color: '#fff', ...F }}>
                        İptal Et
                      </button>
                    </>
                  )}
                  {/* Teacher can still cancel their own confirmed appointments. */}
                  {a.status === 'confirmed' && a._createdByTeacher && (
                    <button
                      onClick={() => teacherApi.updateAppointment(a.id, 'cancelled').then(load).catch(console.error)}
                      className="flex-1 rounded-[10px] py-2 text-[13px] font-semibold hover:opacity-90"
                      style={{ background: '#e5599b', color: '#fff', ...F }}>
                      Randevuyu İptal Et
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}

      {/* ── Create Appointment Modal ── */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setCreateOpen(false)} />
          <div className="relative bg-[#f6f6f6] rounded-[20px] w-full max-w-[600px] max-h-[90vh] overflow-y-auto p-7"
            style={{ boxShadow: '0px 4px 16px rgba(0,0,0,0.15)' }}>
            <CloseX onClick={() => setCreateOpen(false)} />

            {createDone ? (
              <div className="text-center py-6">
                <div className="w-20 h-20 rounded-full bg-[#3973bf] flex items-center justify-center mx-auto mb-4">
                  <svg width="36" height="28" viewBox="0 0 60 45" fill="none">
                    <path d="M5 22L22 39L55 6" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p style={{ ...F, fontSize: 18, fontWeight: 700, color: '#2b2b2b', marginBottom: 20 }}>Randevu Başarıyla Oluşturuldu</p>
                <button onClick={() => setCreateOpen(false)}
                  className="rounded-[12px] px-8 py-3 hover:opacity-90" style={{ background: '#3973bf' }}>
                  <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>Kapat</span>
                </button>
              </div>
            ) : (
              <>
                <h2 style={{ ...F, fontSize: 22, fontWeight: 700, color: '#3973bf', marginBottom: 20 }}>Randevu Oluştur</h2>

                {/* Title */}
                <div className="mb-4">
                  <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>
                    Başlık <span className="text-[#e5599b]">*</span>
                  </label>
                  <input value={aptTitle} onChange={e => setAptTitle(e.target.value)}
                    placeholder="Örn: Haftalık Değerlendirme Görüşmesi"
                    className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[15px]"
                    style={{ border: '2px solid #3973bf', ...F }} />
                </div>

                {/* Target type */}
                <div className="mb-4">
                  <label style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 8 }}>Hedef <span className="text-[#e5599b]">*</span></label>
                  <div className="flex gap-3 mb-3">
                    {(['student', 'parent', 'class'] as const).map(t => (
                      <button key={t} onClick={() => setTargetType(t)}
                        className="flex-1 py-2.5 rounded-[10px] text-[14px] font-semibold transition-colors"
                        style={{ background: targetType === t ? '#3973bf' : '#e5e5e5', color: targetType === t ? '#fff' : '#2b2b2b', ...F }}>
                        {t === 'student' ? 'Tek Öğrenci' : t === 'parent' ? 'Veli' : 'Sınıf'}
                      </button>
                    ))}
                  </div>

                  {loadingStudents ? (
                    <div className="flex justify-center py-4"><div className="w-6 h-6 border-3 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
                  ) : (targetType === 'student' || targetType === 'parent') ? (
                    <div ref={studentSearchRef} className="relative">
                      <input
                        type="text"
                        value={studentDropOpen ? studentQuery : (selectedStudent ? `${selectedStudent.name} (${selectedStudent.className})` : studentQuery)}
                        onChange={e => { setStudentQuery(e.target.value); setStudentDropOpen(true); setSelectedStudentId(''); }}
                        onFocus={() => { setStudentDropOpen(true); if (selectedStudent) setStudentQuery(''); }}
                        placeholder="Öğrenci ara veya seç…"
                        className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px]"
                        style={{ border: '2px solid #3973bf', ...F }}
                      />
                      {selectedStudentId && (
                        <button
                          onClick={() => { setSelectedStudentId(''); setStudentQuery(''); setStudentDropOpen(true); }}
                          aria-label="Temizle"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-[18px] w-6 h-6"
                        >×</button>
                      )}
                      {studentDropOpen && (
                        <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-[12px] shadow-lg border border-gray-200 z-20 max-h-[240px] overflow-y-auto">
                          {studentMatches.length === 0 ? (
                            <p className="text-center text-gray-400 py-3 text-[13px]">Eşleşen öğrenci bulunamadı.</p>
                          ) : (
                            studentMatches.map(s => (
                              <button
                                key={s.id}
                                onClick={() => {
                                  setSelectedStudentId(s.id);
                                  setStudentQuery('');
                                  setStudentDropOpen(false);
                                }}
                                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                                style={{ ...F }}
                              >
                                <p className="text-[14px] font-semibold text-[#2b2b2b]">{s.name}</p>
                                <p className="text-[12px] text-gray-500">{s.className}</p>
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <select value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)}
                      className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px] appearance-none"
                      style={{ border: '2px solid #3973bf', ...F }}>
                      <option value="">Sınıf seçin…</option>
                      {uniqueClasses.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  )}

                  {/* Parent picker — which guardian of the selected student to send it to */}
                  {targetType === 'parent' && selectedStudentId && (() => {
                    const parents = selectedStudent?.parents ?? [];
                    if (parents.length === 0) return <p className="text-[13px] text-red-500 mt-2" style={F}>Bu öğrencinin kayıtlı velisi yok.</p>;
                    if (parents.length === 1) return <p className="text-[13px] text-gray-600 mt-2" style={F}>Veli: <strong>{parents[0].name}</strong></p>;
                    return (
                      <div className="mt-2">
                        <label className="block text-[12px] font-semibold text-gray-500 mb-1" style={F}>Veli</label>
                        <select value={selectedParentId} onChange={e => setSelectedParentId(e.target.value)}
                          className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px] appearance-none" style={{ border: '2px solid #3973bf', ...F }}>
                          <option value="">Veli seçin…</option>
                          {parents.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                    );
                  })()}
                </div>

                {/* Date + Time */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="col-span-1">
                    <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>
                      Tarih <span className="text-[#e5599b]">*</span>
                    </label>
                    <input type="date" value={aptDate} onChange={e => setAptDate(e.target.value)}
                      className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
                      style={{ border: '2px solid #3973bf', ...F }} />
                  </div>
                  <div>
                    <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Başlangıç</label>
                    <input type="time" value={aptStartTime} onChange={e => setAptStartTime(e.target.value)}
                      className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
                      style={{ border: '2px solid #3973bf', ...F }} />
                  </div>
                  <div>
                    <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Bitiş</label>
                    <input type="time" value={aptEndTime} onChange={e => setAptEndTime(e.target.value)}
                      className="w-full bg-white rounded-[12px] px-3 py-2.5 outline-none text-[14px]"
                      style={{ border: '2px solid #3973bf', ...F }} />
                  </div>
                </div>

                {/* B7: teacher's own availability for the picked date */}
                {aptDate && (
                  <div className={`rounded-[12px] px-4 py-2.5 mb-4 text-[13px] ${conflict ? 'bg-[#fdecef] border border-[#e5599b]' : 'bg-[#eef4ff] border border-[#3973bf]'}`} style={F}>
                    {busy.length === 0 ? (
                      <span className="text-[#2b6a2b]">O gün başka randevunuz yok, seçtiğiniz saat uygun.</span>
                    ) : (
                      <>
                        <span className="font-semibold text-[#2b2b2b]">O gün dolu saatleriniz: </span>
                        <span className="text-[#2b2b2b]">{formatBusyRanges(busy)}</span>
                        {conflict && <p className="text-[#c53030] font-semibold mt-1">Bu saatte zaten bir randevunuz var. Lütfen farklı bir saat seçin.</p>}
                      </>
                    )}
                  </div>
                )}

                {/* Format */}
                <div className="mb-4">
                  <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Görüşme Formatı</label>
                  <div className="flex gap-2">
                    {[['face_to_face', 'Yüz Yüze'], ['online', 'Online'], ['phone', 'Telefon']].map(([v, l]) => (
                      <button key={v} onClick={() => setAptFormat(v)}
                        className="flex-1 py-2 rounded-[10px] text-[13px] font-semibold"
                        style={{ background: aptFormat === v ? '#e5599b' : '#e5e5e5', color: aptFormat === v ? '#fff' : '#2b2b2b', ...F }}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div className="mb-5">
                  <label style={{ ...F, fontSize: 13, fontWeight: 600, color: '#2b2b2b', display: 'block', marginBottom: 6 }}>Not <span className="text-[#e5599b]">*</span></label>
                  <textarea value={aptNotes} onChange={e => setAptNotes(e.target.value)}
                    rows={2} placeholder="Görüşme konusu veya ek bilgi…"
                    className="w-full bg-white rounded-[12px] px-4 py-3 outline-none text-[14px] resize-none"
                    style={{ border: '2px solid #3973bf', ...F }} />
                </div>

                {createError && <p style={{ ...F, fontSize: 13, color: '#e5599b', marginBottom: 12 }}>{createError}</p>}

                <div className="flex gap-3">
                  <button onClick={() => setCreateOpen(false)}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90" style={{ background: '#e5e5e5' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 600, color: '#2b2b2b' }}>İptal</span>
                  </button>
                  <button onClick={handleCreate} disabled={submitting || !aptDate || !aptStartTime || !aptEndTime || !aptNotes || conflict || (!selectedStudentId && !selectedClassId && !selectedParentId)}
                    className="flex-1 rounded-[12px] py-3 hover:opacity-90 disabled:opacity-50" style={{ background: '#3973bf' }}>
                    <span style={{ ...F, fontSize: 15, fontWeight: 700, color: '#fafafa' }}>
                      {submitting ? 'Oluşturuluyor…' : conflict ? 'Saat Dolu' : 'Randevu Oluştur'}
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAppointments;
